import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@core/services/auth/auth.service';
import { I18nService, StoreService } from '@core/services';
import { Store } from '@ngrx/store';
import { CookieService } from 'ngx-cookie-service';
import { AUTH_CHANGE_PASSWORD } from '@store/actions';
import { ComponentEnum, LanguajeEnum } from '@core/enums';
import { selectLanguaje } from '@store/selectors';
import { IAbstractPresenter, ILanguageObject } from '@core/interfaces';
import { IPasswordRequirement, IResetPasswordStrategyComponent, IResetPasswordStrategyPresenter } from './interfaces';
import { AuthModalComponentEnum } from '../../../enums';
import { Observable, takeUntil } from 'rxjs';
import { IAuthModalCallback } from '../../../interfaces';

@Injectable({ providedIn: 'root' })
export class ResetPasswordStrategyPresenter
	extends IAbstractPresenter<IResetPasswordStrategyComponent>
	implements IResetPasswordStrategyPresenter
{
	token: string = '';
	newPassword: string = '';
	confirmNewPassword: string = '';
	loading: boolean = false;
	errorMessage: string | null = null;
	successMessage: string | null = null;
	passwordChanged: boolean = false;
	invalidToken: boolean = false;
	languageEnum$: Observable<LanguajeEnum>;
	passwordRequirements: IPasswordRequirement = {
		minLength: false,
		hasUpperCase: false,
		hasLowerCase: false,
		hasNumber: false,
		hasSpecialChar: false,
	};
	passwordsMatch: boolean = false;
	translations: ILanguageObject = {};
	visitingCheckout: boolean = false;
	callback!: IAuthModalCallback;

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private authService: AuthService,
		private storeService: StoreService,
		private store: Store,
		private cookieService: CookieService,
		private i18nService: I18nService
	) {
		super();

		this.languageEnum$ = this.store.select(selectLanguaje);
	}

	dispatchActions(): void {}

	suscribeObservers(): void {
		this.languageEnum$.pipe(takeUntil(this.destroy$)).subscribe(lang => {
			this.view.translations = this.i18nService.getTranslations(ComponentEnum.ResetPassword, lang);
		});

		this.route.queryParamMap.subscribe(params => {
			this.view.token = params.get('token') || '';

			// Verificar si el token está presente
			if (!this.view.token) {
				this.view.invalidToken = true;
				this.view.errorMessage = 'El enlace de recuperación no es válido o ha expirado.';
			}
		});
	}

	checkPasswordRequirements(password: string): void {
		this.view.passwordRequirements = {
			minLength: password.length >= 8,
			hasUpperCase: /[A-Z]/.test(password),
			hasLowerCase: /[a-z]/.test(password),
			hasNumber: /[0-9]/.test(password),
			hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
		};
		this.checkPasswordsMatch();
	}

	checkPasswordsMatch(): void {
		this.view.passwordsMatch =
			this.view.newPassword === this.view.confirmNewPassword &&
			this.view.newPassword !== '' &&
			this.view.confirmNewPassword !== '';
	}

	isPasswordValid(): boolean {
		return Object.values(this.view.passwordRequirements).every(req => req) && this.view.passwordsMatch;
	}

	onPasswordChange(): void {
		this.checkPasswordRequirements(this.view.newPassword);
	}

	onConfirmPasswordChange(): void {
		this.checkPasswordsMatch();
	}

	onResetPassword(): void {
		if (this.view.invalidToken) return;

		if (!this.view.newPassword || !this.view.confirmNewPassword) {
			this.view.errorMessage = 'Por favor ingrese y confirme su nueva contraseña';
			return;
		}

		if (!this.view.passwordsMatch) {
			this.view.errorMessage = 'Las contraseñas no coinciden';
			return;
		}

		if (!this.isPasswordValid()) {
			this.view.errorMessage = 'La contraseña debe cumplir con todos los requisitos de seguridad';
			return;
		}

		this.view.loading = true;
		this.view.errorMessage = null;

		// Guardar el token en una cookie HTTP segura
		const expiryDate = new Date();
		expiryDate.setHours(expiryDate.getHours() + 1); // Expira en 1 hora
		this.cookieService.set(
			'reset_password_token',
			this.view.token,
			expiryDate,
			'/',
			'',
			true, // HttpOnly
			'Strict' // SameSite
		);

		this.store.dispatch(
			AUTH_CHANGE_PASSWORD({
				password: this.view.newPassword,
				token: this.view.token,
			})
		);

		this.authService.changePasswordWithToken(this.view.token, this.view.newPassword).subscribe({
			next: response => {
				this.view.loading = false;
				if (response?.success) {
					this.view.successMessage = '¡Contraseña cambiada exitosamente!';
					this.view.passwordChanged = true;
					// Eliminar la cookie después de un cambio exitoso
					this.cookieService.delete('reset_password_token', '/');
				} else {
					this.view.errorMessage = response?.message || 'Error al cambiar la contraseña';
				}
			},
			error: err => {
				this.view.loading = false;
				this.view.errorMessage = err.error?.message || 'Ocurrió un error al cambiar la contraseña';
				// Eliminar la cookie en caso de error
				this.cookieService.delete('reset_password_token', '/');
			},
		});
	}

	navigateToSignIn(): void {
		this.navigateTo(AuthModalComponentEnum.LOGIN);
	}

	navigateTo(route: AuthModalComponentEnum): void {
		this.view.callback?.navigate(route);
	}
}
